import { Injectable, inject } from '@angular/core';
import {
  ApiDependencyProviderService,
  CollectionRequest,
  CollectionResponse,
  HttpParams,
} from '@web/common';
import { map, Observable } from 'rxjs';
import { AdminCosmeticCollectionItemResponse } from '../responses/admin-cosmetic-collection-item.response';
import { blank, Guid, valueOrDefault } from '@web/utils';
import { AdminCosmeticCreateRequest } from '../requests/admin-cosmetic-create.request';
import { AdminCosmeticDetailsResponse } from '../responses/admin-cosmetic-details.response';
import { AdminCosmeticUpdateRequest } from '../requests/admin-cosmetic-update.request';

@Injectable({
  providedIn: 'root',
})
export class AdminCosmeticApiService {
  private readonly provider = inject(ApiDependencyProviderService);

  public fetchCollection(options: {
    request: CollectionRequest;
    cacheable?: boolean;
    skipByLoading?: boolean;
  }): Observable<CollectionResponse<AdminCosmeticCollectionItemResponse>> {
    const path = 'admin/cosmetic';
    const url = this.provider.httpUrlFactory.withQueryParams(
      this.hostUrl(),
      path,
      this.provider.httpHelper.collectionRequestToQueryParams(options.request),
    );
    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .withOptionalSkipByLoadingInterceptor(() =>
        valueOrDefault(options?.skipByLoading, false),
      )
      .withOptionalCacheInterceptor(() =>
        valueOrDefault(options?.cacheable, false),
      )
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(
        map(
          this.provider.httpHelper.bodyToCollectionResponse(
            AdminCosmeticCollectionItemResponse,
          ),
        ),
      );
  }

  /* prettier-ignore */
  public removeById(id: Guid): Observable<void> {
    const path = `admin/cosmetic/${HttpParams.Id}`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, { [HttpParams.Id]: id });

    return this.provider.http.delete(url).pipe(blank())
  }

  public create(request: AdminCosmeticCreateRequest): Observable<void> {
    const path = `admin/cosmetic`;
    const url = this.provider.httpUrlFactory.simple(this.hostUrl(), path);
    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .build();
    const body = this.provider.httpHelper.requestToBody(
      request,
      AdminCosmeticCreateRequest,
    );

    return this.provider.http.post(url, body, { context }).pipe(blank());
  }

  /* prettier-ignore */
  public fetchDetailsById(id: Guid): Observable<AdminCosmeticDetailsResponse> {
    const path = `admin/cosmetic/${HttpParams.Id}`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, { [HttpParams.Id]: id });
    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .skipByLoadingInterceptor()
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(
        map(
          this.provider.httpHelper.bodyToResponse(AdminCosmeticDetailsResponse),
        ),
      );
  }

  /* prettier-ignore */
  public update(
    id: Guid,
    request: AdminCosmeticUpdateRequest,
  ): Observable<void> {
    const path = `admin/cosmetic/${HttpParams.Id}`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, { [HttpParams.Id]: id });
    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .build();
    const body = this.provider.httpHelper.requestToBody(
      request,
      AdminCosmeticUpdateRequest,
    );

    return this.provider.http.put(url, body, { context }).pipe(blank());
  }

  private hostUrl(): string {
    return this.provider.environment.ApiHostUrl.Shop;
  }
}
